import * as path from 'node:path';
import type { ActiveAbility } from './types.js';
import type { AbilityDiskReader } from './head.js';
import type { AbilityFsWriter, AbilitySweepWriter } from './writer.js';
import type { InstalledMarker } from './materialize.js';
import type { DiscVerdict } from './verify.js';
import { materializeAbility, materializeAbilityParts, sweepOrphanAbilities, computeChecksum, type ArchiveUnpackOpts, type FormatOpts } from './materialize.js';
import { computeDriveHeadFromDisk } from './head.js';
import { verifyDiscOnDisk } from './verify.js';
import { partKeyOf, buildPartsIndex, type PartRef } from './parts.js';

/**
 * Outcome of one convergence pass over the abilities drive (spec I4:
 * repair-then-serve). Every field is loud on purpose — callers surface
 * `tampered`/`refused` rather than swallowing them.
 */
export interface ConvergeReport {
  /** Extras deleted by set-reconciliation (on disk, absent from the shelf). */
  removed: string[];
  /** Discs force re-materialized (missing / divergent / tampered). */
  repaired: string[];
  /** Subset of `repaired` whose at-rest verdict was `'tampered'` (loud). */
  tampered: string[];
  /** Discs whose repair threw — served WITHOUT them, loudly (never fatal). */
  refused: string[];
  /** Post-converge disk-grounded drive head. */
  driveHead: string;
}

/**
 * Everything the converge pass touches, injected. This module is home-agnostic
 * by design (spec I5): no flag logic, no `process.env`, no sandbox imports — the
 * laptop runner and the sandbox both drive it through these deps.
 */
export interface ConvergeDeps {
  /** The shelf snapshot (feed payload) that disk should converge toward. */
  active: ActiveAbility[];
  root: string;
  reader: AbilityDiskReader;
  writer: AbilityFsWriter & AbilitySweepWriter;
  /**
   * When true (session start), each surviving disc is byte-verified at rest and
   * a tamper forces a rebuild. Cheap paths pass false to skip the read.
   */
  deepVerify: boolean;
  /** Ability Format v2: enables layout:v4 checksums + part-granular repair. */
  formatV2?: boolean;
  /** HOOK_FINGERPRINT_V1: skills hash their whole served entry (FormatOpts.hookFingerprint). */
  hookFingerprint?: boolean;
  /**
   * Injected per-environment storage-asset materializers, threaded into every
   * repair (whole-disc AND parts). Absent → a bundled skill degrades to SKILL.md
   * + inline and its marker is poisoned so a later pass retries (spec: degraded,
   * not corrupt). The laptop/sandbox supply these; the server materialize path
   * calls materializeAbility directly with its own injections.
   */
  unpackArchive?: (key: string, destSkillDirAbsPath: string) => Promise<boolean>;
  writeAsset?: (key: string, destAbsPath: string) => Promise<boolean>;
}

/** Per-slug facts gathered from disk, fed to the pure {@link planConvergence}. */
export interface AbilityConvergeProbe {
  slug: string;
  /** Parsed `marker.checksum`, or null when the marker is missing/unparseable. */
  markerChecksum: string | null;
  /**
   * `computeChecksum(active, fmt)` for this slug — computed under the
   * SAME flag the converge pass threads, so it matches the marker's own stamp
   * (the marker was written by materialize under that flag). Mixing flags across
   * the two sides would read as spurious drift.
   */
  activeChecksum: string;
  /** `verifyDiscOnDisk` verdict, or null when `deepVerify` was skipped. */
  verdict: DiscVerdict | null;
}

/** The repair decision, split out so it is unit-testable without any I/O. */
export interface ConvergePlan {
  /** Slugs to force re-materialize, in probe order. */
  repair: string[];
  /** Subset of `repair` triggered by an at-rest tamper. */
  tampered: string[];
}

/**
 * Pure repair decision (spec I4 rules, verbatim): repair when the marker is
 * missing OR its checksum ≠ the active shelf's checksum OR (deep verify saw a
 * tamper). A missing/unparseable marker surfaces as a null `markerChecksum`, so
 * one inequality covers both the missing case and genuine drift (the active
 * checksum is always a real hex digest). `'no-map'` is deliberately NOT a
 * trigger — a legacy marker without a content map stays valid and upgrades to
 * v2 on its next real materialize.
 */
export function planConvergence(probes: AbilityConvergeProbe[]): ConvergePlan {
  const repair: string[] = [];
  const tampered: string[] = [];
  for (const p of probes) {
    const markerStale = p.markerChecksum !== p.activeChecksum;
    const tamperedVerdict = p.verdict === 'tampered';
    if (markerStale || tamperedVerdict) {
      repair.push(p.slug);
      if (tamperedVerdict) tampered.push(p.slug);
    }
  }
  return { repair, tampered };
}

/**
 * Part-granular repair decision (P4a). wholeDisc when the marker predates
 * v3 (no parts map), when the change touches the files:root part, or when a
 * changed/removed part is any kind OTHER than skill/agent/files — part-level
 * surgery (materializeAbilityParts) only knows how to slice skill/agent paths,
 * so an mcp/policy/lessons/settings part (minted in later phases) must take the
 * proven whole-disc path rather than be mis-sliced by partRoot.
 */
export function planPartRepairs(
  markerParts: Record<string, string> | undefined,
  activeParts: PartRef[],
): { changed: PartRef[]; removedKeys: string[]; wholeDisc: boolean } {
  if (!markerParts) return { changed: [], removedKeys: [], wholeDisc: true };
  const activeKeys = new Set(activeParts.map((p) => partKeyOf(p)));
  const changed = activeParts.filter((p) => markerParts[partKeyOf(p)] !== p.hash);
  const removedKeys = Object.keys(markerParts).filter((k) => !activeKeys.has(k));
  const touchesFilesRoot =
    changed.some((p) => p.kind === 'files') || removedKeys.includes('files:root');
  const unknownRemoval = removedKeys.some(
    (k) => !k.startsWith('skill:') && !k.startsWith('agent:') && k !== 'files:root',
  );
  const unknownChange = changed.some((p) => p.kind !== 'skill' && p.kind !== 'agent' && p.kind !== 'files');
  return { changed, removedKeys, wholeDisc: touchesFilesRoot || unknownRemoval || unknownChange };
}

/**
 * Stable stand-in slug reported in `refused` for a feed entry so malformed that
 * even its slug is unreadable (no `.ability`, non-string/empty slug). Callers
 * surfacing `refused` can rely on this literal.
 */
export const UNKNOWN_SLUG = 'unknown';

/** Safely extract an entry's slug; null when the entry is too malformed to name. */
function readSlug(a: ActiveAbility): string | null {
  const slug = (a as { ability?: { slug?: unknown } } | null | undefined)?.ability?.slug;
  return typeof slug === 'string' && slug ? slug : null;
}

/** Read a disc's parsed `.installed` marker, tolerating missing/unreadable/corrupt. */
async function readMarker(dir: string, reader: AbilityDiskReader): Promise<InstalledMarker | null> {
  try {
    const raw = await reader.readFile(path.posix.join(dir, '.installed'));
    if (!raw) return null;
    return JSON.parse(raw) as InstalledMarker;
  } catch {
    return null;
  }
}

/**
 * Reconcile the abilities drive against the active shelf: delete extras, then
 * repair every disc that is missing, checksum-divergent, or (under deepVerify)
 * tampered at rest. Probes AND repairs run per-slug under try/catch, so a
 * single failure — including a malformed feed entry that breaks checksum
 * computation — refuses only that disc and the pass still serves the rest;
 * this function NEVER throws.
 */
export async function convergeAbilities(deps: ConvergeDeps): Promise<ConvergeReport> {
  const { active, root, reader, writer, deepVerify } = deps;
  // Normalize the optional flag to a definite boolean so it threads cleanly into
  // FormatOpts (exactOptionalPropertyTypes); `false` and `undefined` are
  // byte-identical downstream (both → layout:v3 checksum + v2 marker).
  const formatV2 = deps.formatV2 ?? false;
  // The checksum formula, built ONCE: the probe, the whole-disc repair and the part
  // repair all stamp/compare under it (a marker written under another formula would
  // read as drift and re-repair every pass). Absent → byte-identical.
  const fmt: FormatOpts = { formatV2, ...(deps.hookFingerprint ? { hookFingerprint: true } : {}) };
  // exactOptionalPropertyTypes: build the bag by conditional spread so an absent
  // callback is truly absent, never an explicit `undefined`.
  const assetOpts: ArchiveUnpackOpts = {
    ...(deps.unpackArchive ? { unpackArchive: deps.unpackArchive } : {}),
    ...(deps.writeAsset ? { writeAsset: deps.writeAsset } : {}),
  };
  const refused: string[] = [];

  // 0) Partition the feed. An entry whose slug is unreadable can be neither
  //    swept around nor repaired — refuse it up front under UNKNOWN_SLUG and
  //    converge the rest (spec I4: refuse only the failed disc).
  const entries: Array<{ slug: string; item: ActiveAbility }> = [];
  for (const item of active) {
    const slug = readSlug(item);
    if (slug === null) refused.push(UNKNOWN_SLUG);
    else entries.push({ slug, item });
  }

  // 1) Set-reconciliation: drop discs on disk that the shelf no longer carries.
  //    sweepOrphanAbilities is internally guarded (per-folder try/catch) and
  //    never throws.
  const removed = await sweepOrphanAbilities(root, entries.map((e) => e.slug), writer);

  // 2) Probe each active disc from disk. The WHOLE probe body is guarded:
  //    computeChecksum over a malformed feed entry (null id/version,
  //    non-iterable files, ...) throws a TypeError, and that class of failure
  //    must refuse the one disc, never sink the pass.
  const probes: AbilityConvergeProbe[] = [];
  // The parsed marker per slug, captured in the SAME guarded read that derives
  // markerChecksum (one `.installed` read per disc), reused by the repair pass
  // for its `parts` map. Only successfully-parsed markers land here.
  const markersBySlug = new Map<string, InstalledMarker>();
  for (const { slug, item } of entries) {
    try {
      const dir = path.posix.join(root, slug);
      let verdict: DiscVerdict | null = null;
      if (deepVerify) {
        try { verdict = await verifyDiscOnDisk(dir, reader); }
        catch { verdict = null; } // an unreadable verify never fabricates a tamper
      }
      const marker = await readMarker(dir, reader);
      if (marker) markersBySlug.set(slug, marker);
      probes.push({
        slug,
        markerChecksum:
          typeof marker?.checksum === 'string' && marker.checksum ? marker.checksum : null,
        activeChecksum: computeChecksum(item, fmt),
        verdict,
      });
    } catch {
      refused.push(slug);
    }
  }

  // 3) Decide (pure), then 4) execute — one refused disc must not sink the rest.
  const plan = planConvergence(probes);
  const bySlug = new Map(entries.map((e) => [e.slug, e.item]));
  const repaired: string[] = [];
  for (const slug of plan.repair) {
    const a = bySlug.get(slug);
    if (!a) continue;
    try {
      const probe = probes.find((p) => p.slug === slug);
      const marker = markersBySlug.get(slug) ?? null; // from the probe pass
      const tamperedDisc = probe?.verdict === 'tampered';
      // Surgical part-level repair only when v2 is on, we have a parsed marker,
      // and the disc is NOT tampered (untrusted disk always takes whole-disc).
      if (formatV2 && marker && !tamperedDisc) {
        const partPlan = planPartRepairs(marker.parts, buildPartsIndex(a));
        if (!partPlan.wholeDisc) {
          await materializeAbilityParts(a, root, writer, partPlan, marker, { ...assetOpts, ...fmt });
          repaired.push(slug);
          continue;
        }
      }
      await materializeAbility(a, root, writer, { force: true, ...fmt, ...assetOpts });
      repaired.push(slug);
    } catch {
      refused.push(slug);
    }
  }
  // tampered is loud only for discs we actually rebuilt (a refused tamper stays refused).
  const tampered = plan.tampered.filter((s) => repaired.includes(s));

  // 5) Ground the head on what is now on disk. computeDriveHeadFromDisk is
  //    internally guarded and never throws.
  const driveHead = await computeDriveHeadFromDisk(root, reader);

  return { removed, repaired, tampered, refused, driveHead };
}
